import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAsyncResource } from 'use-async-resource';
import dateFormat from 'dateformat';

import { CustomToastContent } from '../../components/CustomToastContent';
import { AuthContext } from '../../context/AuthContext';
import { BranchesContext } from '../../context/BranchesContext';
import { NewRequestContext } from '../../context/NewRequestContext';
import { BranchListItem } from '../../models/dashboard-models';
import { ManualHandout } from '../../models/warehouse-models';
import { toastOptionsTopRight } from '../../utils/Enums/ToastOptions';
import { fetchAllDashboardBranchesByDistributor } from '../../utils/dashboard-hooks';
import { usePagination } from '../../utils/pagination-hooks';
import { useGetUsers } from '../../utils/users-hooks';
import { fetchDashboardBranchById } from '../../utils/dashboard-hooks';
import {
    UseExportBranches,
    useGetClients,
    useGetNewRequestBranchesByClient,
    getDevicesCountByStatus,
    getDevicesCountByStatusSummary,
    getDevicesCountByStatusPrices,
} from '../../utils/clients-hooks';
import { NewRequest } from '../NewRequest';
import { DevicesTab } from './DevicesTab';
import { DistributorOption, DistributorTransferTab } from './DistributorTransferTab';
import {
    DeviceCountDevice,
    DeviceCountBranchRow,
    DeviceCountResponse,
    DevicePricesByDistributorRow,
    DevicePricesResponse,
    DeviceSummaryResponse,
    ISelectItems,
    MaintenanceStatus,
    MaintenanceStatusFilter,
} from './types';
import { normalizeDeviceRows } from './deviceUtils';

const getDistributorOptionLabel = (distributor: DistributorOption) =>
    distributor.assignedDistributorName || distributor.username;

export const CustomersInner = () => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { loggedUser, group } = useContext(AuthContext);

    const [getDistributors] = useAsyncResource(
        useGetUsers,
        group.mainGroup !== undefined
            ? { filter: { tags: [], search: group.mainGroup + '-' + group.subGroup } }
            : undefined,
        // 'DISTRIBUTOR'
       'DISTRIBUTOR,INSTALLER'

    );
    const distributors = getDistributors();
    const distributorOptions = useMemo(
        () => ({
            items: distributors.items.reduce<DistributorOption[]>(
                (options, { id, username, name, assignedDistributorName }) => {
                    if (id !== undefined) {
                        options.push({ id, username, assignedDistributorName: assignedDistributorName || name });
                    }

                    return options;
                },
                []
            ),
        }),
        [distributors.items]
    );

    const { control } = useForm<ManualHandout>({
        mode: 'all',
    });
    const [distributorOne, setDistributorOne] = useState<number>();
    const [distributorTwo, setDistributorTwo] = useState<number>();
    const { branches } = useContext(BranchesContext);
    const allBranches = useMemo(() => branches ?? [], [branches]);
    const [distributor1Branches, setDistributor1Branches] = useState<BranchListItem[]>([]);
    const [distributor2Branches, setDistributor2Branches] = useState<BranchListItem[]>([]);
    const [isLoadingDistributor1, setIsLoadingDistributor1] = useState(false);
    const [isLoadingDistributor2, setIsLoadingDistributor2] = useState(false);
    const [isTransferringToDistributor1, setIsTransferringToDistributor1] = useState(false);
    const [isTransferringToDistributor2, setIsTransferringToDistributor2] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [search, setSearch] = useState('');
    const [selectedItems1, setSelectedItems1] = useState<BranchListItem[]>([]);
    const [selectedItems2, setSelectedItems2] = useState<BranchListItem[]>([]);
    const [selectedItems11, setSelectedItems11] = useState<ISelectItems>({});
    const [selectedItems22, setSelectedItems22] = useState<ISelectItems>({});
    const firstDistributorRequestIdRef = useRef(0);
    const secondDistributorRequestIdRef = useRef(0);
    const { selectedBranch, onBranchSelect, onClientSelect } = useContext(NewRequestContext);
    const [readClients] = useAsyncResource(useGetClients, {});
    const allClients = readClients();

    const [showObjectModal, setShowObjectModal] = useState(false);

    useEffect(() => {
        const storedPosition = JSON.parse(localStorage.getItem('scrollPosition') || '0');
        if (storedPosition) {
            window.scrollTo({ top: storedPosition, behavior: 'smooth' });
        }
    }, [showObjectModal]);

    const mergeUniqueBranches = useCallback((branchesToMerge: BranchListItem[]) => {
        const uniqueBranches = new Map<number, BranchListItem>();

        branchesToMerge.forEach((branch) => {
            uniqueBranches.set(branch.id, branch);
        });

        return Array.from(uniqueBranches.values());
    }, []);

    const FetchData = async (branchSource: BranchListItem | DeviceCountBranchRow) => {
        const currentScrollPosition = window.scrollY;
        localStorage.setItem('scrollPosition', JSON.stringify(currentScrollPosition));

        const mapBranch = allBranches.find((branch) => branch.id === branchSource.id);
        const dashboardBranch = await fetchDashboardBranchById(branchSource.id);
        const branchDetails = { ...branchSource, ...mapBranch, ...dashboardBranch };
        const branchCompanyName = branchDetails.companyName;
        const client = allClients?.items.find((client) => client.companyName === branchCompanyName);
        const id = client?.id || 0;
        const branches = await useGetNewRequestBranchesByClient(id);
        const fetchedBranch = branches?.items.find((branch) => branch.id === branchSource.id);
        let branch = {
            ...fetchedBranch,
            paymentDate: branchDetails.paymentDate,
            nextMaintenanceDate:
                branchDetails.nextMaintenanceDate ??
                selectedBranch.nextMaintenanceDate ??
                fetchedBranch?.nextMaintenanceDate,
        };
        const distributor = distributors.items.find((user) => user?.id === branchDetails.assignedDistributor);

        onClientSelect(client);
        onBranchSelect({ ...branch, assignedDistributor: distributor });
        setShowObjectModal(true);
        localStorage.setItem('isModalOpen', JSON.stringify(true));
        localStorage.setItem('selectedClient', JSON.stringify(client));
    };

    const hadleFirstDistrbutor = async (distributorId: number) => {
        if (!distributorId || distributorId === distributorTwo) {
            return;
        }

        const requestId = ++firstDistributorRequestIdRef.current;
        setDistributorOne(distributorId);
        setSelectedItems1([]);
        setSelectedItems11({});
        setDistributor1Branches([]);
        setIsLoadingDistributor1(true);

        try {
            const branches = await fetchAllDashboardBranchesByDistributor(distributorId);

            if (requestId !== firstDistributorRequestIdRef.current) {
                return;
            }

            setDistributor1Branches(branches);
        } finally {
            if (requestId === firstDistributorRequestIdRef.current) {
                setIsLoadingDistributor1(false);
            }
        }
    };

    const hadleSecondDistrbutor = async (distributorId: number) => {
        if (!distributorId || distributorId === distributorOne) {
            return;
        }

        const requestId = ++secondDistributorRequestIdRef.current;
        setDistributorTwo(distributorId);
        setSelectedItems2([]);
        setSelectedItems22({});
        setDistributor2Branches([]);
        setIsLoadingDistributor2(true);

        try {
            const branches = await fetchAllDashboardBranchesByDistributor(distributorId);

            if (requestId !== secondDistributorRequestIdRef.current) {
                return;
            }

            setDistributor2Branches(branches);
        } finally {
            if (requestId === secondDistributorRequestIdRef.current) {
                setIsLoadingDistributor2(false);
            }
        }
    };

    const filteredFirstTable = useMemo(
        () =>
            distributor1Branches
                .filter(
                    (branch) =>
                        branch.name.toLowerCase().includes(search.toLowerCase()) ||
                        branch.companyName.toLowerCase().includes(search.toLowerCase())
                )
                .sort((a, b) => {
                    if (a.companyName < b.companyName) return -1;
                    if (a.companyName > b.companyName) return 1;
                    if (a.companyName === b.companyName) {
                        if (a.name < b.name) return -1;
                        if (a.name > b.name) return 1;
                    }
                    return 0;
                }),
        [distributor1Branches, search]
    );

    const filteredSecondTable = useMemo(
        () =>
            distributor2Branches
                .filter(
                    (branch) =>
                        branch.name.toLowerCase().includes(search.toLowerCase()) ||
                        branch.companyName.toLowerCase().includes(search.toLowerCase())
                )
                .sort((a, b) => {
                    if (a.companyName < b.companyName) return -1;
                    if (a.companyName > b.companyName) return 1;
                    if (a.companyName === b.companyName) {
                        if (a.name < b.name) return -1;
                        if (a.name > b.name) return 1;
                    }
                    return 0;
                }),
        [distributor2Branches, search]
    );

    const transferBranches = useCallback(async ({
        branchesToTransfer,
        targetDistributor,
        sourceSetter,
        targetSetter,
        clearSelectedBranches,
        clearSelectedMap,
        setTransferLoading,
    }: {
        branchesToTransfer: BranchListItem[];
        targetDistributor?: number;
        sourceSetter: React.Dispatch<React.SetStateAction<BranchListItem[]>>;
        targetSetter: React.Dispatch<React.SetStateAction<BranchListItem[]>>;
        clearSelectedBranches: React.Dispatch<React.SetStateAction<BranchListItem[]>>;
        clearSelectedMap: React.Dispatch<React.SetStateAction<ISelectItems>>;
        setTransferLoading: React.Dispatch<React.SetStateAction<boolean>>;
    }) => {
        if (!branchesToTransfer.length || !targetDistributor || !loggedUser) {
            return;
        }

        const updatedItems = branchesToTransfer.map((branch) => ({
            ...branch,
            assignedDistributor: targetDistributor,
        }));

        setTransferLoading(true);

        try {
            await UseExportBranches(updatedItems, Number(loggedUser.id));

            targetSetter((current) => mergeUniqueBranches([...updatedItems, ...current]));

            const transferredIds = new Set(updatedItems.map((branch) => branch.id));

            sourceSetter((current) => current.filter((branch) => !transferredIds.has(branch.id)));
            clearSelectedBranches([]);
            clearSelectedMap({});

            toast.success(
                <CustomToastContent content={[`Успешно прехвърлени ${updatedItems.length} обекта.`]} />,
                toastOptionsTopRight
            );
        } finally {
            setTransferLoading(false);
        }
    }, [loggedUser, mergeUniqueBranches]);

    const exportToDistributor2 = useCallback(async () => {
        await transferBranches({
            branchesToTransfer: selectedItems1,
            targetDistributor: distributorTwo,
            sourceSetter: setDistributor1Branches,
            targetSetter: setDistributor2Branches,
            clearSelectedBranches: setSelectedItems1,
            clearSelectedMap: setSelectedItems11,
            setTransferLoading: setIsTransferringToDistributor2,
        });
    }, [distributorTwo, selectedItems1, transferBranches]);

    const exportToDistributor1 = useCallback(async () => {
        await transferBranches({
            branchesToTransfer: selectedItems2,
            targetDistributor: distributorOne,
            sourceSetter: setDistributor2Branches,
            targetSetter: setDistributor1Branches,
            clearSelectedBranches: setSelectedItems2,
            clearSelectedMap: setSelectedItems22,
            setTransferLoading: setIsTransferringToDistributor1,
        });
    }, [distributorOne, selectedItems2, transferBranches]);

    const selectAllFirstTable = useCallback(() => {
        const nextSelectedItems = filteredFirstTable.reduce<ISelectItems>((acc, branch) => {
            acc[branch.id] = true;
            return acc;
        }, {});

        setSelectedItems11(nextSelectedItems);
        setSelectedItems1(filteredFirstTable);
    }, [filteredFirstTable]);

    const deselectAllFirstTable = useCallback(() => {
        setSelectedItems11({});
        setSelectedItems1([]);
    }, []);

    const selectAllSecondTable = useCallback(() => {
        const nextSelectedItems = filteredSecondTable.reduce<ISelectItems>((acc, branch) => {
            acc[branch.id] = true;
            return acc;
        }, {});

        setSelectedItems22(nextSelectedItems);
        setSelectedItems2(filteredSecondTable);
    }, [filteredSecondTable]);

    const deselectAllSecondTable = useCallback(() => {
        setSelectedItems22({});
        setSelectedItems2([]);
    }, []);

    const [activeTab, setActiveTab] = useState('first');
    const [devices, setDevices] = useState<DeviceCountBranchRow[] | null>(null);
    const [devicesSummary, setDevicesSummary] = useState<DeviceSummaryResponse | null>(null);
    const [devicesPrices, setDevicesPrices] = useState<DevicePricesResponse | null>(null);
    const [devicesLoading, setDevicesLoading] = useState(false);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [pricesLoading, setPricesLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [selectedDistributor, setSelectedDistributor] = useState<number | null>(null);
    const [selectedDeviceModel, setSelectedDeviceModel] = useState<string | null>(null);
    const [selectedMaintenanceSort, setSelectedMaintenanceSort] = useState<MaintenanceStatusFilter | null>(null);
    const currentDateRange = useMemo(() => {
        const now = new Date();
        return {
            fromDate: dateFormat(new Date(now.getFullYear(), now.getMonth(), 1), 'yyyy-mm-dd'),
            toDate: dateFormat(new Date(now.getFullYear(), now.getMonth() + 1, 0), 'yyyy-mm-dd'),
        };
    }, []);
    const [selectedFromDate, setSelectedFromDate] = useState(currentDateRange.fromDate);
    const [selectedToDate, setSelectedToDate] = useState(currentDateRange.toDate);
    const pagination = usePagination(10);
    const { pageSize, currentPage, setCurrentPage, setPagination } = pagination;
    const [totalElements, setTotalElements] = useState(0);
    const distributorsById = useMemo(
        () =>
            new Map(
                distributorOptions.items.map((distributor) => [distributor.id, getDistributorOptionLabel(distributor)])
            ),
        [distributorOptions.items]
    );
    const selectedDateRangeLabel = useMemo(() => {
        if (!selectedFromDate || !selectedToDate) return '';
        return `${dateFormat(selectedFromDate, 'dd.mm.yyyy')} - ${dateFormat(selectedToDate, 'dd.mm.yyyy')}`;
    }, [selectedFromDate, selectedToDate]);
    const selectedDeviceSearch = selectedDeviceModel?.trim() ?? '';
    const effectiveDeviceSearch = selectedDeviceSearch.length >= 3 ? selectedDeviceSearch : '';
    const selectedMaintenanceLabel = useMemo(() => {
        if (!selectedMaintenanceSort) {
            return '';
        }

        const labels: Record<MaintenanceStatusFilter, string> = {
            pending: t('withoutMaintenanceStatus'),
            paused: t('pausedObjects'),
            serviced: t('servicedThisMonth'),
            overdue: t('missedMaintenance'),
            upcoming: t('upcomingMaintenanceInFiveDays'),
        };

        return labels[selectedMaintenanceSort];
    }, [selectedMaintenanceSort, t]);
    const summaryFilterText = useMemo(() => {
        const filters = [];
        const distributorName = distributorOptions.items.find(
            (distributor) => distributor.id === selectedDistributor
        );

        if (distributorName) {
            filters.push(`${t('distributor')}: ${getDistributorOptionLabel(distributorName)}`);
        }

        if (effectiveDeviceSearch) {
            filters.push(`${t('searchButton')}: ${effectiveDeviceSearch}`);
        }

        if (selectedDateRangeLabel) {
            filters.push(`${t('period')}: ${selectedDateRangeLabel}`);
        }

        if (selectedMaintenanceLabel) {
            filters.push(`${t('maintenance')}: ${selectedMaintenanceLabel}`);
        }

        return filters.length > 0 ? filters.join(' | ') : t('all');
    }, [
        distributorOptions.items,
        effectiveDeviceSearch,
        selectedDistributor,
        selectedMaintenanceLabel,
        selectedDateRangeLabel,
        t,
    ]);
    const pricesFilterText = useMemo(() => {
        const filters = [];

        if (selectedDateRangeLabel) {
            filters.push(`${t('period')}: ${selectedDateRangeLabel}`);
        }

        return filters.length > 0 ? filters.join(' | ') : t('all');
    }, [selectedDateRangeLabel, t]);
    const getMaintenanceStatus = useCallback(
        (branch: DeviceCountBranchRow): MaintenanceStatus | null => branch.branchState ?? null,
        []
    );
    const getMaintenanceRowStyle = (branch: DeviceCountBranchRow): React.CSSProperties => {
        const maintenanceStatus = getMaintenanceStatus(branch);
        const getRowBackground = (color: string) =>
            ({
                '--bs-table-bg': color,
                '--bs-table-accent-bg': color,
                backgroundColor: color,
                cursor: 'pointer',
            } as React.CSSProperties);

        if (maintenanceStatus === 'paused') {
            return getRowBackground('#e2e3e5');
        }

        if (maintenanceStatus === 'serviced') {
            return getRowBackground('#d1e7dd');
        }

        if (maintenanceStatus === 'overdue') {
            return getRowBackground('#f8d7da');
        }

        if (maintenanceStatus === 'upcoming') {
            return getRowBackground('#fff3cd');
        }

        return { cursor: 'pointer' };
    };
    const getMaintenanceFilterParam = useCallback((filter: MaintenanceStatusFilter | null) => {
        if (!filter) {
            return {};
        }

        return { maintenanceStatusSort: filter };
    }, []);

    const matchesMaintenanceFilter = useCallback(
        (branch: DeviceCountBranchRow, filter: MaintenanceStatusFilter) => {
            const status = getMaintenanceStatus(branch);

            return filter === 'pending' ? status === null : status === filter;
        },
        [getMaintenanceStatus]
    );

    const sortedDevices = useMemo(() => {
        if (!devices || !selectedMaintenanceSort) {
            return devices;
        }

        return [...devices].sort((a, b) => {
            const aMatches = matchesMaintenanceFilter(a, selectedMaintenanceSort);
            const bMatches = matchesMaintenanceFilter(b, selectedMaintenanceSort);

            if (aMatches === bMatches) return 0;
            return aMatches ? -1 : 1;
        });
    }, [devices, matchesMaintenanceFilter, selectedMaintenanceSort]);
    const getBranchCompanyName = (branch: DeviceCountBranchRow) =>
        branch.companyName ?? allBranches.find((item) => item.id === branch.id)?.companyName ?? '-';

    const groupDevicesById = (branchDevices: DeviceCountDevice[]) =>
        Array.from(
            branchDevices
                .reduce((groups, device) => {
                    const group = groups.get(device.id) ?? [];
                    group.push(device);
                    groups.set(device.id, group);
                    return groups;
                }, new Map<number, DeviceCountDevice[]>())
                .entries()
        );

    const devicesFilterParams = useMemo(() => {
        const params: any = {};

        if (selectedDistributor) {
            params.distributorId = selectedDistributor;
        }
        if (effectiveDeviceSearch) {
            params.search = effectiveDeviceSearch;
        }
        if (selectedFromDate) {
            params.fromDate = selectedFromDate;
        }
        if (selectedToDate) {
            params.toDate = selectedToDate;
        }

        return params;
    }, [effectiveDeviceSearch, selectedDistributor, selectedFromDate, selectedToDate]);

    const fetchDevices = useCallback(async () => {
        try {
            setDevicesLoading(true);
            const pageParams = {
                ...devicesFilterParams,
                ...getMaintenanceFilterParam(selectedMaintenanceSort),
                page: currentPage - 1,
                size: pageSize,
                sort: 'model,asc',
            };

            const res = (await getDevicesCountByStatus(pageParams)) as unknown as DeviceCountResponse;

            setDevices(normalizeDeviceRows(res.content));
            setTotalElements(res.totalElements);
        } catch (err) {
            console.error('Error fetching devices', err);
        } finally {
            setDevicesLoading(false);
        }
    }, [currentPage, devicesFilterParams, getMaintenanceFilterParam, pageSize, selectedMaintenanceSort]);

    const fetchDevicesSummary = useCallback(async () => {
        try {
            setDevicesSummary(null);
            setSummaryLoading(true);
            const summaryRes = (await getDevicesCountByStatusSummary({
                ...devicesFilterParams,
                ...getMaintenanceFilterParam(selectedMaintenanceSort),
            })) as unknown as DeviceSummaryResponse;

            setDevicesSummary(summaryRes);
        } catch (err) {
            console.error('Error fetching devices summary', err);
            setDevicesSummary(null);
        } finally {
            setSummaryLoading(false);
        }
    }, [devicesFilterParams, getMaintenanceFilterParam, selectedMaintenanceSort]);

    const fetchDevicesPrices = useCallback(async () => {
        try {
            setDevicesPrices(null);
            setPricesLoading(true);
            const pricesRes = (await getDevicesCountByStatusPrices({
                fromDate: selectedFromDate,
                toDate: selectedToDate,
            })) as unknown as DevicePricesResponse | DevicePricesByDistributorRow[];

            setDevicesPrices(Array.isArray(pricesRes) ? { pricesByDistributor: pricesRes } : pricesRes);
        } catch (err) {
            console.error('Error fetching devices prices', err);
            setDevicesPrices(null);
        } finally {
            setPricesLoading(false);
        }
    }, [selectedFromDate, selectedToDate]);

    const fetchDevicesForExport = useCallback(async () => {
        try {
            setExportLoading(true);
            const res = (await getDevicesCountByStatus({
                ...devicesFilterParams,
                ...getMaintenanceFilterParam(selectedMaintenanceSort),
                page: 0,
                size: Math.max(totalElements, pageSize, 1000),
                sort: 'model,asc',
            })) as unknown as DeviceCountResponse;

            return normalizeDeviceRows(res.content);
        } catch (err) {
            console.error('Error fetching devices for export', err);
            return [];
        } finally {
            setExportLoading(false);
        }
    }, [devicesFilterParams, getMaintenanceFilterParam, pageSize, selectedMaintenanceSort, totalElements]);

    useEffect(() => {
        if (activeTab === 'second') {
            fetchDevices();
        }
    }, [activeTab, fetchDevices]);

    useEffect(() => {
        if (activeTab === 'second') {
            fetchDevicesSummary();
            fetchDevicesPrices();
        }
    }, [activeTab, fetchDevicesPrices, fetchDevicesSummary]);

    return (
        <div className='d-flex flex-column px-4 mt-3 w-100 bg-light w-100 align-items-center flex-grow-1'>
            <div className='d-flex border-bottom justify-content-center mb-3 p-3 px-1 w-100'>
                <div className='d-flex w-100 justify-content-between'>
                    <div
                        role='button'
                        className={`d-flex justify-content-center w-50 step-nav-link text-center ${
                            activeTab === 'first' ? 'active' : ''
                        }`}
                        onClick={() => setActiveTab('first')}
                    >
                        <h5>{t('distributor1To2')}</h5>
                    </div>
                    <div
                        role='button'
                        className={`d-flex justify-content-center w-50 step-nav-link text-center ${
                            activeTab === 'second' ? 'active' : ''
                        }`}
                        onClick={() => setActiveTab('second')}
                    >
                        <h5>{t('devices')}</h5>
                    </div>
                </div>
            </div>
            {activeTab === 'first' && (
                <DistributorTransferTab
                    t={t}
                    control={control}
                    distributors={distributorOptions}
                    searchInputRef={searchInputRef}
                    search={search}
                    setSearch={setSearch}
                    hadleFirstDistrbutor={hadleFirstDistrbutor}
                    hadleSecondDistrbutor={hadleSecondDistrbutor}
                    exportToDistributor1={exportToDistributor1}
                    exportToDistributor2={exportToDistributor2}
                    selectAllFirstTable={selectAllFirstTable}
                    deselectAllFirstTable={deselectAllFirstTable}
                    selectAllSecondTable={selectAllSecondTable}
                    deselectAllSecondTable={deselectAllSecondTable}
                    distributorOne={distributorOne}
                    distributorTwo={distributorTwo}
                    isLoadingDistributor1={isLoadingDistributor1}
                    isLoadingDistributor2={isLoadingDistributor2}
                    isTransferringToDistributor1={isTransferringToDistributor1}
                    isTransferringToDistributor2={isTransferringToDistributor2}
                    filteredFirstTable={filteredFirstTable}
                    filteredSecondTable={filteredSecondTable}
                    selectedItems1={selectedItems1}
                    selectedItems2={selectedItems2}
                    selectedItems11={selectedItems11}
                    selectedItems22={selectedItems22}
                    setSelectedItems1={setSelectedItems1}
                    setSelectedItems2={setSelectedItems2}
                    setSelectedItems11={setSelectedItems11}
                    setSelectedItems22={setSelectedItems22}
                    FetchData={FetchData}
                />
            )}
            {activeTab === 'second' && (
                <DevicesTab
                    t={t}
                    pageSize={pageSize}
                    setPagination={setPagination}
                    setCurrentPage={setCurrentPage}
                    exportLoading={exportLoading}
                    fetchDevicesForExport={fetchDevicesForExport}
                    distributors={distributorOptions}
                    selectedDistributor={selectedDistributor}
                    setSelectedDistributor={setSelectedDistributor}
                    selectedDeviceModel={selectedDeviceModel}
                    setSelectedDeviceModel={setSelectedDeviceModel}
                    selectedFromDate={selectedFromDate}
                    setSelectedFromDate={setSelectedFromDate}
                    selectedToDate={selectedToDate}
                    setSelectedToDate={setSelectedToDate}
                    selectedMaintenanceSort={selectedMaintenanceSort}
                    setSelectedMaintenanceSort={setSelectedMaintenanceSort}
                    sortedDevices={sortedDevices}
                    devicesLoading={devicesLoading}
                    getMaintenanceRowStyle={getMaintenanceRowStyle}
                    FetchData={FetchData}
                    getBranchCompanyName={getBranchCompanyName}
                    groupDevicesById={groupDevicesById}
                    distributorsById={distributorsById}
                    totalElements={totalElements}
                    pagination={pagination}
                    summaryLoading={summaryLoading}
                    devicesSummary={devicesSummary}
                    pricesLoading={pricesLoading}
                    devicesPrices={devicesPrices}
                    summaryFilterText={summaryFilterText}
                    pricesFilterText={pricesFilterText}
                />
            )}
            {showObjectModal && <NewRequest showModal={showObjectModal} setShowModal={setShowObjectModal} />}
        </div>
    );
};
